import { z } from 'zod';
import { AS400_CDB_COLUMN, type As400ExportRow } from '@erp/rpc';
import { asCellText } from './as400ExportParts';

/**
 * Rows put away on the review grid (GEN-2715): a reviewer done with some rows,
 * or with a whole part, hides them (select rows → context menu) and moves on.
 *
 * Hidden rows are kept for the whole review, like the sort, so a row hidden on
 * one worksheet is hidden on every worksheet and the tabs keep lining up:
 * `records` by item and CDB key ({@link rowRecordKey}) — the rows themselves —
 * and `items` by item number — every row of the part, on every page, however
 * a sort spread them. Hiding only takes rows off screen. They stay in the
 * review, in its validation and in what it publishes.
 */
// Past this many, hiding adds nothing more (the saved session would otherwise
// fail to restore, taking the sort and filters with it).
export const MAX_HIDDEN_ROW_ENTRIES = 5000;

export const HiddenRowsSchema = z.object({
  records: z.array(z.string().max(200)).max(MAX_HIDDEN_ROW_ENTRIES),
  items: z.array(z.string().max(100)).max(MAX_HIDDEN_ROW_ENTRIES),
});
export type HiddenRows = z.infer<typeof HiddenRowsSchema>;

export const NO_HIDDEN_ROWS: HiddenRows = { records: [], items: [] };

export function hasHiddenRows(hidden: HiddenRows): boolean {
  return hidden.records.length > 0 || hidden.items.length > 0;
}

/** What the grid needs to hide the review's rows from its context menu. */
export interface GridRowHiding {
  /** The worksheet's item-number column — what a row's part is read from. */
  itemColumn: string;
  hidden: HiddenRows;
  onHiddenChange: (hidden: HiddenRows) => void;
  /** Already-translated menu labels (Handsontable renders outside React). */
  menuLabels: {
    hideSelected: string;
    /** "Hide all '…' rows", named after the selection's item numbers. */
    hideItems: (itemNumbers: readonly string[]) => string;
  };
}

/**
 * The part a row belongs to: its live item number (an edit to the number
 * doesn't move the row to another part), else an authored row's staged one.
 * A draft row has neither yet.
 */
export function rowItemNumber(
  row: As400ExportRow,
  itemColumn: string,
): string | null {
  return (
    asCellText(row.values[itemColumn]) ??
    asCellText(row.stagedValues?.[itemColumn])
  );
}

/**
 * The record a row is, the same on every worksheet: its item and its CDB key.
 * A row without an item number (a draft, whose number is still a pending
 * edit) is only itself, by its grid key — never its division's other drafts.
 */
export function rowRecordKey(row: As400ExportRow, itemColumn: string): string {
  return recordKey(row, rowItemNumber(row, itemColumn));
}

function recordKey(row: As400ExportRow, itemNumber: string | null): string {
  if (itemNumber === null) return `row:${row.key}`;
  const cdb = asCellText(row.values[AS400_CDB_COLUMN]);
  return `${itemNumber}\u0000${cdb ?? ''}`;
}

/** The indexes of the page's rows that are hidden. */
export function hiddenRowIndexes(
  rows: readonly As400ExportRow[],
  itemColumn: string,
  hidden: HiddenRows,
): number[] {
  if (!hasHiddenRows(hidden)) return [];
  const records = new Set(hidden.records);
  const items = new Set(hidden.items);
  return rows.flatMap((row, index) => {
    const itemNumber = rowItemNumber(row, itemColumn);
    const isHidden =
      (itemNumber !== null && items.has(itemNumber)) ||
      records.has(recordKey(row, itemNumber));
    return isHidden ? [index] : [];
  });
}

/** The distinct item numbers of these rows, in row order; drafts have none. */
export function itemNumbersAt(
  rows: readonly As400ExportRow[],
  itemColumn: string,
  rowIndexes: readonly number[],
): string[] {
  const itemNumbers = new Set<string>();
  for (const index of rowIndexes) {
    const row = rows[index];
    const itemNumber = row ? rowItemNumber(row, itemColumn) : null;
    if (itemNumber !== null) itemNumbers.add(itemNumber);
  }
  return [...itemNumbers];
}

/** What the grid's context menu does with a selection of row indexes. */
export interface RowHidingActions {
  hideSelected: (rowIndexes: readonly number[]) => void;
  hideItems: (rowIndexes: readonly number[]) => void;
  /** The "hide all rows of these parts" label, or null when no row has a part. */
  hideItemsLabel: (rowIndexes: readonly number[]) => string | null;
}

/**
 * The menu actions for one page of rows. Each hides on top of what's hidden
 * already, up to {@link MAX_HIDDEN_ROW_ENTRIES}, and changes nothing when the
 * selection adds nothing.
 */
export function rowHidingActions(
  rows: readonly As400ExportRow[],
  { itemColumn, hidden, onHiddenChange, menuLabels }: GridRowHiding,
): RowHidingActions {
  return {
    hideSelected(rowIndexes) {
      const records = new Set(hidden.records);
      for (const index of rowIndexes) {
        if (records.size >= MAX_HIDDEN_ROW_ENTRIES) break;
        const row = rows[index];
        if (row) records.add(rowRecordKey(row, itemColumn));
      }
      if (records.size > hidden.records.length) {
        onHiddenChange({ ...hidden, records: [...records] });
      }
    },
    hideItems(rowIndexes) {
      const items = new Set(hidden.items);
      for (const itemNumber of itemNumbersAt(rows, itemColumn, rowIndexes)) {
        if (items.size >= MAX_HIDDEN_ROW_ENTRIES) break;
        items.add(itemNumber);
      }
      if (items.size > hidden.items.length) {
        onHiddenChange({ ...hidden, items: [...items] });
      }
    },
    hideItemsLabel(rowIndexes) {
      const itemNumbers = itemNumbersAt(rows, itemColumn, rowIndexes);
      return itemNumbers.length > 0 ? menuLabels.hideItems(itemNumbers) : null;
    },
  };
}
